import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Copy, Dice5, MapPin, Search, Target, ChevronDown, Lock, Crown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { applyMirror, mirrorDigit, mirrorVariants } from "@/lib/lotteryLogic";
import { useAuth } from "@/contexts/AuthContext";

const applyPairSwap = (digits, pair) => {
  if (!pair) return digits;
  const a = Number(pair[0]);
  const b = Number(pair[1]);
  return digits.map((d) => (d === a ? b : d === b ? a : d));
};

const prettyCells = (cells) =>
  cells.map(([c, r]) => `C${c + 1}R${r + 1}`).join(" → ");

export const HitsPanel = ({
  hits,
  anchorCount,
  anchorDigits = [],
  targetLength = 4,
  activePair,
  globalMirrorOn,
  starred,
  onToggleStar,
  luckyNumber,
  onLuckyPick,
  seedDigits,
  dateSumDigit,
}) => {
  const [limit, setLimit] = useState(60);
  const [filter, setFilter] = useState("");
  const [straightOnly, setStraightOnly] = useState(false);
  const [drawCheck, setDrawCheck] = useState("");
  const [drawCheckerOpen, setDrawCheckerOpen] = useState(false);
  const { user } = useAuth();
  const isPro = user && user.plan && user.plan !== "free";

  const hitSet = useMemo(() => {
    const s = new Set();
    hits.forEach((h) => s.add(h.combo));
    return s;
  }, [hits]);

  // Draw Checker: user enters a lotto draw (e.g. "16,23,25,33,37,41").
  // Single-digit tokens are padded with a leading zero to match targetLength
  // (so a Mega Millions "6" becomes "06"). Each padded number is matched
  // against the hit set directly or via any mirror substitution of its digits.
  const drawResults = useMemo(() => {
    if (!drawCheck.trim()) return [];
    const tokens = drawCheck
      .split(/[\s,;/]+/)
      .map((n) => n.replace(/\D/g, ""))
      .filter((n) => n.length > 0 && n.length <= targetLength);
    return tokens.map((raw) => {
      const padded = raw.padStart(targetLength, "0");
      if (hitSet.has(padded)) return { num: raw, padded, found: true, via: "direct" };
      const variants = mirrorVariants(padded).filter((v) => v !== padded);
      const match = variants.find((v) => hitSet.has(v));
      if (match) return { num: raw, padded, found: true, via: `mirror → ${match}` };
      return { num: raw, padded, found: false };
    });
  }, [drawCheck, hitSet, targetLength]);

  const transformed = useMemo(() => {
    const base = hits.map((h) => {
      let digits = h.combo.split("").map(Number);
      if (globalMirrorOn) digits = applyMirror(digits);
      digits = applyPairSwap(digits, activePair);
      return { ...h, display: digits.join("") };
    });
    const clean = filter.replace(/\D/g, "");
    const variants = clean ? new Set(mirrorVariants(clean)) : null;
    return base.filter((h) => {
      if (straightOnly && !h.isStraight) return false;
      if (variants && !variants.has(h.display)) return false;
      return true;
    });
  }, [hits, activePair, globalMirrorOn, filter, straightOnly]);

  const copyNumber = async (n) => {
    try {
      await navigator.clipboard.writeText(n);
      toast.success(`Copied ${n}`);
    } catch {
      toast.error("Clipboard blocked");
    }
  };

  const mirrorOfSum = mirrorDigit(dateSumDigit);

  return (
    <Card className="bg-[#131c2e]/90 border-slate-800 shadow-xl shadow-black/40">
      <CardContent className="p-5 sm:p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-100">
              Where To Look · Possible Hits
            </h3>
            <p className="text-xs text-slate-500 mt-1 font-mono-digit">
              seed {seedDigits.join("")} · anchors{" "}
              <span className="text-emerald-300">
                {anchorDigits.length > 0 ? anchorDigits.join(" ") : `${dateSumDigit}/${mirrorOfSum}`}
              </span>{" "}
              · {anchorCount} cells · {transformed.length} reads
            </p>
          </div>
          <Button
            type="button"
            data-testid="lucky-pick-btn"
            onClick={isPro ? onLuckyPick : undefined}
            disabled={!isPro}
            asChild={!isPro}
            className={`font-heading font-bold gap-2 h-11 px-5 ${
              isPro
                ? "bg-amber-500 hover:bg-amber-400 text-slate-950"
                : "bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
            }`}
          >
            {isPro ? (
              <>
                <Dice5 className="w-4 h-4" />
                Lucky Pick
              </>
            ) : (
              <Link to="/upgrade" data-testid="lucky-pick-upgrade">
                <Lock className="w-4 h-4" />
                Lucky Pick · Pro
              </Link>
            )}
          </Button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="hits-filter-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Find a number (mirror variants auto-match)"
              className="h-10 pl-9 bg-[#0b0f17] border-slate-700 font-mono-digit tracking-widest"
            />
          </div>
          <button
            type="button"
            data-testid="straight-only-toggle"
            onClick={() => setStraightOnly((v) => !v)}
            className={`text-xs font-mono-digit uppercase tracking-wider px-3 h-10 rounded-lg border transition-all ${
              straightOnly
                ? "bg-emerald-500/15 border-emerald-500/60 text-emerald-200"
                : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"
            }`}
          >
            {straightOnly ? "Straight only" : "Straight + Box"}
          </button>
        </div>

        {/* Draw Checker - Pro-gated, collapsible */}
        <div className="rounded-xl border border-slate-800 bg-[#0b0f17]/60">
          <button
            type="button"
            data-testid="draw-checker-toggle"
            onClick={() => {
              if (!isPro) {
                toast.message("Draw Checker is a Pro feature", {
                  description: "Verify any lotto draw against your workout. Upgrade to unlock.",
                  action: { label: "See plans", onClick: () => (window.location.href = "/upgrade") },
                });
                return;
              }
              setDrawCheckerOpen((v) => !v);
            }}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-slate-800/30 transition-colors rounded-xl"
          >
            <div className="flex items-center gap-2">
              {isPro ? (
                <Target className="w-4 h-4 text-amber-400" />
              ) : (
                <Lock className="w-4 h-4 text-slate-500" />
              )}
              <span className="text-xs uppercase tracking-widest text-slate-300 font-heading">
                Verify a Draw {!isPro && <span className="text-amber-400">· Pro</span>}
              </span>
              {drawResults.length > 0 && !drawCheckerOpen && isPro && (
                <span
                  data-testid="draw-check-summary-collapsed"
                  className="text-[10px] font-mono-digit text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 px-2 py-0.5 rounded"
                >
                  {drawResults.filter((r) => r.found).length} / {drawResults.length} hit
                </span>
              )}
            </div>
            {isPro ? (
              <ChevronDown
                className={`w-4 h-4 text-slate-500 transition-transform ${
                  drawCheckerOpen ? "rotate-180" : ""
                }`}
              />
            ) : (
              <Crown className="w-4 h-4 text-amber-400" />
            )}
          </button>

          {drawCheckerOpen && (
            <div className="px-4 pb-4 space-y-3 border-t border-slate-800/60">
              <p className="pt-3 text-[11px] text-slate-500 font-mono-digit">
                Paste any draw — mirror substitution is applied automatically.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  data-testid="draw-check-input"
                  value={drawCheck}
                  onChange={(e) => setDrawCheck(e.target.value)}
                  placeholder="e.g. 2, 9, 21, 41, 57, 5"
                  className="h-10 bg-[#131c2e] border-slate-700 font-mono-digit tracking-wider"
                />
                <Button
                  type="button"
                  data-testid="draw-check-clear"
                  variant="outline"
                  onClick={() => setDrawCheck("")}
                  className="h-10 border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200"
                >
                  Clear
                </Button>
              </div>
              {drawResults.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="draw-check-results">
                  {drawResults.map((r, i) => (
                    <div
                      key={i}
                      data-testid={`draw-check-pill-${i}`}
                      className={`font-mono-digit text-sm px-3 py-1.5 rounded-lg border flex items-center gap-2 ${
                        r.found
                          ? "bg-emerald-500/10 border-emerald-500/50 text-emerald-200"
                          : "bg-rose-500/10 border-rose-500/40 text-rose-200"
                      }`}
                    >
                      <span className="font-bold">{r.num}</span>
                      {r.padded !== r.num && (
                        <span className="text-[10px] opacity-60">({r.padded})</span>
                      )}
                      <span className="text-[10px] uppercase opacity-70">
                        {r.found ? r.via : "miss"}
                      </span>
                    </div>
                  ))}
                  <div
                    data-testid="draw-check-summary"
                    className="font-mono-digit text-xs text-slate-400 px-2 py-1.5"
                  >
                    {drawResults.filter((r) => r.found).length} / {drawResults.length} on the workout
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {luckyNumber && (
          <div
            data-testid="lucky-number"
            className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-5 flex items-center justify-between gap-4"
          >
            <div>
              <div className="text-[11px] uppercase tracking-widest text-amber-300/80 font-heading">
                Your Lucky Pick
              </div>
              <div className="font-mono-digit text-4xl sm:text-5xl font-extrabold text-amber-200 reel-digit tracking-widest mt-1">
                {luckyNumber}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              data-testid="copy-lucky-btn"
              onClick={() => copyNumber(luckyNumber)}
              className="text-amber-200 hover:bg-amber-500/20"
            >
              <Copy className="w-5 h-5" />
            </Button>
          </div>
        )}

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5"
          data-testid="hits-grid"
        >
          {transformed.slice(0, limit).map((h, i) => {
            const isStarred = starred.includes(h.display);
            return (
              <div
                key={`${h.display}-${i}`}
                data-testid={`hit-${i}`}
                className={`group relative rounded-lg border bg-[#0b0f17]/70 hover:border-slate-600 transition-all px-3 py-2.5 ${
                  isStarred
                    ? "border-amber-500/70 bg-amber-500/5"
                    : "border-slate-800"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 font-mono-digit text-xl font-bold tracking-wider">
                    {h.display.split("").map((d, idx) => (
                      <span
                        key={idx}
                        className={
                          idx === h.anchorIdx
                            ? "text-emerald-300 bg-emerald-500/15 border border-emerald-500/50 rounded px-1.5"
                            : "text-slate-200 px-1"
                        }
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      data-testid={`star-hit-${i}`}
                      onClick={() => onToggleStar(h.display)}
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-800"
                      aria-label="Star"
                    >
                      <Star
                        className={`w-4 h-4 ${
                          isStarred ? "fill-amber-400 text-amber-400" : "text-slate-500"
                        }`}
                      />
                    </button>
                    <button
                      type="button"
                      data-testid={`copy-hit-${i}`}
                      onClick={() => copyNumber(h.display)}
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-800"
                      aria-label="Copy"
                    >
                      <Copy className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-slate-500 font-mono-digit flex-wrap">
                  <span
                    className={`uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      h.isStraight
                        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                        : "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                    }`}
                  >
                    {h.isStraight ? "straight" : "box"}
                  </span>
                  <MapPin className="w-3 h-3" />
                  <span className="uppercase tracking-wider">{h.dir}</span>
                  <span className="text-slate-600">·</span>
                  <span>{prettyCells(h.cells)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {transformed.length > limit && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              data-testid="show-more-hits-btn"
              onClick={() => setLimit((l) => l + 60)}
              className="border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200"
            >
              Show more ({transformed.length - limit} remaining)
            </Button>
          </div>
        )}

        {starred.length > 0 && (
          <div className="pt-2 border-t border-slate-800">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-heading mb-2">
              Starred ({starred.length})
            </div>
            <div className="flex flex-wrap gap-2" data-testid="starred-list">
              {starred.map((n) => (
                <Badge
                  key={n}
                  variant="outline"
                  className="font-mono-digit text-base px-3 py-1.5 border-amber-500/50 bg-amber-500/10 text-amber-200"
                >
                  {n}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
